import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import IconButton from 'components/IconButton/IconButton';
import useToggle from 'hooks/useToggle';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import FormLabel from './FormLabel';

export const StyledInput = styled(InputBase)(({ theme, error }) => ({
  'label + &': {
    marginTop: theme.spacing(0.5),
  },
  // '& .MuiButton-contained': {
  //   borderRadius: 12,
  // },
  height: 'auto',
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  backgroundColor: theme.palette.input.background,
  border: '1px solid',
  borderColor: error ? theme.palette.error.main : theme.palette.dividerVariant,
  transition: theme.transitions.create(['border-color']),
  '&:hover': {
    borderColor: error
      ? theme.palette.error.main
      : theme.palette.primary.lighter,
  },
  '&.Mui-focused': {
    borderColor: error
      ? theme.palette.error.main
      : theme.palette.input.highlight,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  '&.MuiInputBase-readOnly': {
    backgroundColor: theme.palette.input.readOnly,
  },
  '& .MuiInputBase-input': {
    borderRadius: theme.shape.borderRadius,
    '&::placeholder': {
      color: theme.palette.text.subtle,
      opacity: 1,
    },
    '&.MuiInputBase-inputAdornedStart': {
      paddingLeft: 0,
    },
    '&.MuiInputBase-inputAdornedEnd': {
      paddingRight: 0,
      '& .MuiButton-root': {
        marginRight: -6,
      },
    },
  },
  '&.MuiInputBase-multiline': {
    // borderRadius: theme.spacing(1),
  },
  '&.MuiInputBase-adornedEnd': {
    paddingRight: '12px',
  },
  '&.MuiInputBase-adornedStart': {
    paddingLeft: '12px',
  },
}));

const Input = React.forwardRef(
  (
    {
      id,
      name,
      label,
      tooltip,
      hint,
      placeholder,
      hasError,
      helpText,
      onChange = noop,
      value,
      onClick,
      onBlur,
      endAdornment,
      startAdornment,
      type,
      inputProps = {},
      multiline,
      rows,
      minRows,
      readOnly,
      disabled,
      ...other
    },
    ref,
  ) => {
    const [showPassword, toggleShowPassword] = useToggle(false);

    const handleChange = (e) => {
      if (type === 'number') {
        const { value, name } = e.target;
        if (value) {
          onChange({
            target: {
              name,
              value:
                value
                  .replace(/(?!^)-|[^0-9.-]/g, '')
                  .match(/^-?\d*\.?\d*$/)?.[0] || '',
            },
          });
        } else {
          onChange(e);
        }
      } else {
        onChange(e);
      }
    };

    const isPassword = type === 'password';
    if (isPassword) {
      inputProps = {
        ...inputProps,
        type: showPassword ? 'text' : 'password',
      };
      endAdornment = (
        <IconButton
          onClick={toggleShowPassword}
          size="small"
          variant="text"
          icon={
            showPassword ? (
              <VisibilityOffOutlinedIcon />
            ) : (
              <VisibilityOutlinedIcon />
            )
          }
        />
      );
    }

    return (
      <FormControl
        variant="standard"
        margin="normal"
        error={hasError}
        {...other}
      >
        {label && (
          <FormLabel label={label} tooltip={tooltip}>
            {label}
          </FormLabel>
        )}
        <StyledInput
          id={id}
          name={name}
          type={type === 'number' ? 'text' : type}
          inputRef={ref}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          multiline={multiline}
          disabled={disabled}
          readOnly={readOnly}
          rows={rows}
          minRows={minRows}
          onBlur={onBlur}
          onClick={onClick}
          error={hasError}
          inputProps={inputProps}
          startAdornment={
            startAdornment && (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            )
          }
          endAdornment={
            endAdornment && (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            )
          }
        />
        {hint && (
          <Typography
            component="p"
            variant="caption"
            color="textSecondary"
            sx={{ pl: 2, pt: 0.5, fontWeight: 500 }}
          >
            {hint}
          </Typography>
        )}
        {hasError && <FormHelperText>{helpText}</FormHelperText>}
      </FormControl>
    );
  },
);

Input.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  hasError: PropTypes.bool,
  helpText: PropTypes.string,
  hint: PropTypes.string,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  type: PropTypes.oneOf(['text', 'password', 'number', 'email']),
  inputProps: PropTypes.object,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  tooltip: PropTypes.string,
  required: PropTypes.bool,
};

StyledInput.propTypes = Input.propTypes;

export default Input;
